import { app, chat, type OpenSingleChatRequest } from '@microsoft/teams-js'
import type { HostKind } from '@/lib/teamsHost'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export type TeamsChatOpenMode = 'teamsjs' | 'teams-deep-link' | 'web-deep-link'

interface TeamsChatDependencies {
  isChatSupported: () => boolean
  openChat: (request: OpenSingleChatRequest) => Promise<void>
  openLink: (url: string) => Promise<void>
  openWindow: (url: string) => void
}

const defaultDependencies: TeamsChatDependencies = {
  isChatSupported: () => chat.isSupported(),
  openChat: (request) => chat.openChat(request),
  openLink: (url) => app.openLink(url),
  openWindow: (url) => { window.open(url, '_blank', 'noopener,noreferrer') },
}

export function normalizeTeamsChatEmail(email: string | null | undefined): string | null {
  const normalized = String(email ?? '').trim().toLowerCase()
  return EMAIL_PATTERN.test(normalized) ? normalized : null
}

/** Official Teams chat deep link. Deliberately omits `message`, so nothing is composed or sent. */
export function buildTeamsChatDeepLink(email: string): string {
  const normalized = normalizeTeamsChatEmail(email)
  if (!normalized) throw new Error('TEAMS_CHAT_EMAIL_INVALID')
  return `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(normalized)}`
}

/**
 * Opens a one-to-one Teams chat without reading or sending messages.
 * Inside Teams the typed chat capability is preferred. Hosts that do not
 * expose it receive the official HTTPS chat deep link through app.openLink.
 * A normal browser opens that same deep link in a separate tab/window.
 */
export async function openTeamsChat(
  email: string,
  host: HostKind,
  dependencies: TeamsChatDependencies = defaultDependencies,
): Promise<TeamsChatOpenMode> {
  const normalized = normalizeTeamsChatEmail(email)
  if (!normalized) throw new Error('TEAMS_CHAT_EMAIL_INVALID')

  const deepLink = buildTeamsChatDeepLink(normalized)
  if (host === 'teams') {
    try {
      if (dependencies.isChatSupported()) {
        await dependencies.openChat({ user: normalized })
        return 'teamsjs'
      }
    } catch {
      // The typed capability can reject for unsupported/external users.
      // Fall through to the official Teams chat deep link.
    }

    await dependencies.openLink(deepLink)
    return 'teams-deep-link'
  }

  dependencies.openWindow(deepLink)
  return 'web-deep-link'
}
